import { NextSeo } from "next-seo";
import { Navbar } from "~/components/Navbar/component";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { FilterOperators, QueryResponse } from "~/modules/filters/types";
import { PageWrapper, SidebarLayout } from "~/components/SidebarLayout";
import { Routes } from "~/routes";
import { Footer } from "~/components/Footer";
import { NEXTJS_REVALIDATE_TIMEOUT } from "~/server/consts";
import {
    filterOutPlatformsWithoutData,
    queryPlatforms,
} from "~/modules/platforms/api/router";
import { DEFAULT_PLATFORM_LIST_QUERY } from "~/modules/platforms/consts";
import { Breadcrumbs } from "~/components/Breadcrumbs";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import { Platform } from "~/modules/platforms";
import { PlatformListPage } from "~/modules/platforms/pages/list/component";
import { Props as AggregateDisplayProps } from "~/components/AggregateDisplay";
import {
    DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO,
    DISTRIBUTION_TYPE_TOOLTIPS,
    TOKENIZATION_TYPE_IDS,
} from "~/consts";
import { getProtocolListPageDescription } from "~/modules/app/seo/ProtocolListSEO";
import { ProtocolListSEO } from "~/modules/app/seo/ProtocolListSEO";
import {
    NetFlowsData,
    queryNetFlows,
} from "~/modules/visualization/util/queryNetFlows";
import {
    TokenTimeseriesColumns,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";
import { ASSET_CLASS_IDS, SOCIAL_OG_IMAGE_URL } from "~/consts";
import { queryTokenAggregateValueWith30D } from "~/modules/token_timeseries/api/queryTokenAggregateValue";
import { MEASURE_IDS } from "~/consts";
import {
    LeagueTableGroupTypes,
    LeagueTableTabsByDistributionType,
} from "~/modules/league_tables/types";
import { queryLeagueTablesByDistributionType } from "~/modules/league_tables/api/queryLeagueTablesByDistributionType";

// // // //

export async function getStaticProps() {
    const [
        listQueryResponseUnfiltered,
        // Aggregate values
        distributedAggregates,
        representedAggregates,
        rwaHoldersAggregates,
        stablecoinValueAggregates,
        stablecoinHoldersAggregates,
        netFlowsWithStablecoins,
        netFlows,
        leagueTableTabs,
        leagueTableTabsWithoutStablecoins,
    ] = await Promise.all([
        queryPlatforms({
            query: {
                name: "platforms-list",
                ...DEFAULT_PLATFORM_LIST_QUERY,
                pagination: {
                    page: 1,
                    perPage: 1000,
                },
            },
        }),
        // Aggregate values
        // 1. Distributed Asset Value
        queryTokenAggregateValueWith30D({
            name: "platforms-list--distributed-asset-value",
            measureID: MEASURE_IDS.BridgedTokenValueDollar,
            filters: [
                {
                    field: TokenTimeseriesColumns.tokenizationTypeID,
                    operator: FilterOperators.Equals,
                    value: TOKENIZATION_TYPE_IDS.OnChainDistributed,
                },
                {
                    field: TokenTimeseriesColumns.assetClassID,
                    operator: FilterOperators.NotEquals,
                    value: ASSET_CLASS_IDS.Stablecoin,
                },
            ],
        }),
        // 2. Represented Asset Value
        queryTokenAggregateValueWith30D({
            name: "platforms-list--represented-asset-value",
            measureID: MEASURE_IDS.BridgedTokenValueDollar,
            filters: [
                {
                    field: TokenTimeseriesColumns.tokenizationTypeID,
                    operator: FilterOperators.Equals,
                    value: TOKENIZATION_TYPE_IDS.OnChainRepresented,
                },
                {
                    field: TokenTimeseriesColumns.assetClassID,
                    operator: FilterOperators.NotEquals,
                    value: ASSET_CLASS_IDS.Stablecoin,
                },
            ],
        }),
        // 3. Total Asset Holders (RWA - excluding stablecoins)
        queryTokenAggregateValueWith30D({
            name: "platforms-list--total-rwa-holders",
            measureID: MEASURE_IDS.DailyNumberOfHolders,
            filters: [
                {
                    field: TokenTimeseriesColumns.assetClassID,
                    operator: FilterOperators.NotEquals,
                    value: ASSET_CLASS_IDS.Stablecoin,
                },
            ],
        }),
        // 4. Total Stablecoin Value
        queryTokenAggregateValueWith30D({
            name: "platforms-list--total-stablecoin-value",
            measureID: MEASURE_IDS.BridgedTokenMarketCapDollar,
            filters: [
                {
                    field: TokenTimeseriesColumns.assetClassID,
                    operator: FilterOperators.Equals,
                    value: ASSET_CLASS_IDS.Stablecoin,
                },
            ],
        }),
        // 5. Total Stablecoin Holders
        queryTokenAggregateValueWith30D({
            name: "platforms-list--total-stablecoin-holders",
            measureID: MEASURE_IDS.DailyNumberOfHolders,
            filters: [
                {
                    field: TokenTimeseriesColumns.assetClassID,
                    operator: FilterOperators.Equals,
                    value: ASSET_CLASS_IDS.Stablecoin,
                },
            ],
        }),
        queryNetFlows({
            name: "platforms-list--net-flows",
            groupBy: TokenTimeseriesGroupBy.protocol,
        }),
        queryNetFlows({
            name: "platforms-list--net-flows-exclude-stablecoins",
            groupBy: TokenTimeseriesGroupBy.protocol,
            filters: [
                {
                    field: TokenTimeseriesColumns.assetClassID,
                    operator: FilterOperators.NotEquals,
                    value: ASSET_CLASS_IDS.Stablecoin,
                },
            ],
        }),
        // League tables by distribution type (with stablecoins) — feeds the pie chart
        queryLeagueTablesByDistributionType({
            name: "platforms-list",
            groups: [LeagueTableGroupTypes.platforms],
        }),
        // League tables by distribution type (without stablecoins) — feeds the pie chart
        queryLeagueTablesByDistributionType({
            name: "platforms-list-exclude-stablecoins",
            filter: {
                field: TokenTimeseriesColumns.assetClassID,
                operator: FilterOperators.NotEquals,
                value: ASSET_CLASS_IDS.Stablecoin,
            },
            groups: [LeagueTableGroupTypes.platforms],
        }),
    ]);

    const listQueryResponse = filterOutPlatformsWithoutData(
        listQueryResponseUnfiltered,
    );

    const aggregates: Array<AggregateDisplayProps> = [
        {
            label: "Distributed Asset Value",
            type: "dollar_compact",
            tooltip: DISTRIBUTION_TYPE_TOOLTIPS.distributed,
            tooltipLinkTo: DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO,
            value: distributedAggregates.value,
            percentChange: {
                value: distributedAggregates.change30D,
                interval: "30d",
            },
        },
        ...(representedAggregates.value > 0
            ? [
                  {
                      label: "Represented Asset Value",
                      type: "dollar_compact",
                      tooltip: DISTRIBUTION_TYPE_TOOLTIPS.represented,
                      tooltipLinkTo:
                          DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO,
                      value: representedAggregates.value,
                      percentChange: {
                          value: representedAggregates.change30D,
                          interval: "30d",
                      },
                  } satisfies AggregateDisplayProps,
              ]
            : []),
        {
            label: "Stablecoin Value",
            type: "dollar_compact",
            value: stablecoinValueAggregates.value,
            percentChange: {
                value: stablecoinValueAggregates.change30D,
                interval: "30d",
            },
        },
        {
            label: "Total RWA Holders",
            type: "count",
            value: rwaHoldersAggregates.value,
            percentChange: {
                value: rwaHoldersAggregates.change30D,
                interval: "30d",
            },
        },
        {
            label: "Total Stablecoin Holders",
            type: "count_compact",
            value: stablecoinHoldersAggregates.value,
            percentChange: {
                value: stablecoinHoldersAggregates.change30D,
                interval: "30d",
            },
        },
        {
            label: "Total Platforms",
            type: "count",
            value: listQueryResponse.results.length,
        },
    ];

    return {
        props: {
            aggregates,
            listQueryResponse,
            netFlows,
            netFlowsWithStablecoins,
            leagueTableTabs,
            leagueTableTabsWithoutStablecoins,
        },
        // Next.js will re-generate this page based on NEXTJS_REVALIDATE_TIMEOUT const
        revalidate: NEXTJS_REVALIDATE_TIMEOUT,
    };
}

// // // //

export default function PlatformsPage(props: {
    aggregates: Array<AggregateDisplayProps>;
    listQueryResponse: QueryResponse<Platform>;
    netFlows: NetFlowsData;
    netFlowsWithStablecoins: NetFlowsData;
    leagueTableTabs: LeagueTableTabsByDistributionType;
    leagueTableTabsWithoutStablecoins: LeagueTableTabsByDistributionType;
}) {
    const {
        aggregates,
        listQueryResponse,
        netFlows,
        netFlowsWithStablecoins,
        leagueTableTabs,
        leagueTableTabsWithoutStablecoins,
    } = props;
    return (
        <PageTrack route={Routes.platforms}>
            <NextSeo
                title="Tokenization Platforms"
                canonical={addBaseUrl(Routes.platforms)}
                description={getProtocolListPageDescription()}
                openGraph={{
                    type: "website",
                    url: addBaseUrl(Routes.platforms),
                    title: "Tokenization Platforms",
                    site_name: "RWA.xyz",
                    description: getProtocolListPageDescription(),
                    images: [
                        {
                            url: SOCIAL_OG_IMAGE_URL,
                            width: 4000,
                            height: 2300,
                            alt: "rwa.xyz",
                        },
                    ],
                }}
                additionalMetaTags={[
                    {
                        name: "keywords",
                        content:
                            "tokenization platforms, RWA platforms, issuance platforms, Securitize, Centrifuge, Ondo, real world assets",
                    },
                ]}
            />
            <ProtocolListSEO />
            <BreadcrumbSEO
                items={[
                    {
                        name: "Tokenization Platforms",
                        href: Routes.platforms,
                    },
                ]}
            />
            <UserFetcher>
                {({ user }) => <Navbar route={Routes.platforms} user={user} />}
            </UserFetcher>
            <SidebarLayout pathname={Routes.platforms}>
                <Breadcrumbs
                    pages={[
                        {
                            name: "Tokenization Platforms",
                            current: false,
                        },
                    ]}
                />
                <PageWrapper>
                    <PlatformListPage
                        aggregates={aggregates}
                        listQueryResponse={listQueryResponse}
                        netFlows={netFlows}
                        netFlowsWithStablecoins={netFlowsWithStablecoins}
                        leagueTableTabs={leagueTableTabs}
                        leagueTableTabsWithoutStablecoins={
                            leagueTableTabsWithoutStablecoins
                        }
                    />
                    <Footer />
                </PageWrapper>
            </SidebarLayout>
        </PageTrack>
    );
}
